import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface RevenueMetrics {
  mrr: number;
  active_subscriptions: number;
  trialing_users: number;
  canceled_last_30d: number;
  churn_rate: number;
  trial_conversion_rate: number;
  total_subscriptions: number;
  new_subs_last_30d: number;
}

export interface AdminSubscription {
  id: string;
  stripe_subscription_id: string;
  status: string;
  price_id: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
  user_id: string;
  user_email: string;
  user_name: string | null;
  user_avatar: string | null;
}

export interface MonthlyData {
  month: string;
  active_count: number;
  new_count: number;
  canceled_count: number;
}

export function useRevenueMetrics() {
  const [metrics, setMetrics] = useState<RevenueMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase.rpc('admin_get_revenue_metrics', {});
    if (signal?.aborted) return;
    if (e) setError(e.message);
    else setMetrics(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchMetrics(controller.signal);
    return () => controller.abort();
  }, [fetchMetrics]);

  return { metrics, loading, error, refetch: fetchMetrics };
}

export function useAdminSubscriptions({ status, search, page = 1 }: { status?: string | null; search?: string; page?: number }) {
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchSubs = async () => {
      setLoading(true);
      setError(null);
      const { data, error: e } = await supabase.rpc('admin_list_subscriptions', {
        p_status: status || null,
        p_search: search || null,
        p_page: page,
        p_per_page: 25,
      });
      if (cancelled) return;
      if (e) setError(e.message);
      else if (data) { setSubscriptions(data.subscriptions ?? []); setTotal(data.total ?? 0); }
      setLoading(false);
    };
    fetchSubs();
    return () => { cancelled = true; };
  }, [status, search, page]);

  return { subscriptions, total, loading, error };
}

export function useRevenueHistory(months: number = 12) {
  const [history, setHistory] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      const { data } = await supabase.rpc('admin_get_revenue_history', { p_months: months });
      if (cancelled) return;
      if (data) setHistory(data);
      setLoading(false);
    };
    fetchHistory();
    return () => { cancelled = true; };
  }, [months]);

  return { history, loading };
}
